import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { fetchProfile, login as loginApi, logout as logoutApi } from '@/apis';
import { useLoading } from '@/contexts/LoadingContext';
import { useApiHandler } from '@/hooks';
import { setupInterceptors } from '@/hooks/useAxios';
import { Profile } from '@/types';
import {
  clearAllUserData,
  deleteToken,
  getToken,
  setToken,
} from '@/utils/tokenStorage';

export interface AuthContextType {
  token: string | null;
  profile: Profile | null;
  profileLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loadProfile: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [tokenState, setTokenState] = useState<string | null>(() => getToken());
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileLoading, setProfileLoading] = useState(() => !!getToken());
  const { handleApiCall } = useApiHandler();
  const { startLoading, stopLoading } = useLoading();

  const saveToken = useCallback((t: string | null) => {
    setTokenState(t);
    if (t) {
      setToken(t);
    } else {
      deleteToken();
    }
  }, []);

  const logout = useCallback(async () => {
    await handleApiCall(() => logoutApi());
    clearAllUserData();
    setTokenState(null);
    setProfile(null);
  }, [handleApiCall]);

  const loadProfile = useCallback(async () => {
    if (!tokenState) return;
    setProfileLoading(true);

    const profileData = await handleApiCall(() => fetchProfile(), {
      // 載入個人資料失敗時不顯示錯誤訊息，因為這是背景操作
    });

    if (profileData) {
      setProfile(profileData);
    }

    setProfileLoading(false);
  }, [tokenState, handleApiCall]);

  const login = useCallback(
    async (email: string, password: string) => {
      const loginData = await handleApiCall(() => loginApi(email, password), {
        successMessage: '登入成功！',
      });

      if (loginData) {
        saveToken(loginData.token);
      } else {
        throw new Error('登入失敗');
      }
    },
    [saveToken, handleApiCall],
  );

  const hasLoggedInRef = useRef(false);

  useEffect(() => {
    if (tokenState) {
      if (!hasLoggedInRef.current) {
        hasLoggedInRef.current = true;
        loadProfile();
      }
    } else {
      hasLoggedInRef.current = false;
      setProfile(null);
    }
  }, [tokenState, loadProfile]);

  useLayoutEffect(() => {
    const teardown = setupInterceptors(
      getToken,
      saveToken,
      logout,
      startLoading,
      stopLoading,
    );
    return teardown;
  }, [logout, saveToken, startLoading, stopLoading]);

  return (
    <AuthContext.Provider
      value={{
        token: tokenState,
        login,
        logout,
        profile,
        profileLoading,
        loadProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
